<template>
    <div 
        id="page-content"
        class="search no-outline"
        :class="{'search__relative':hasScroll}"
        tabindex="0"
        data-no-focus-scroll
    >
        <search-input />

        <div class="search__container">
            <div class="search__transition-container">
                
                <div 
                    v-if="displaySearchResult"
                    class="search__content-wrapper"
                    :class="{'search__content-wrapper_loading': isLoading}"
                >
                    <div v-if="!isLoading" class="search__result-container">
                        <search-not-found v-if="!hasResult"/>

                        <search-result 
                            v-else 
                            ref="searchResultRef" 
                            class="search__result"
                            @ready="ready"   
                            @show-all="showAll"
                            @list-item-selected="listItemSelected"
                        />
                    </div>
    
                    <div v-else class="search__loader-wrapper">
                        <loader class="loader_bigger loader_default-color" />
                    </div> 
                </div>
            </div>

            <div v-if="!displaySearchResult" class="search__no-result search__no-result_visible">
                {{ searchYouCanSearch }}
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, type Ref, computed, onMounted, onBeforeUnmount, provide } from "vue";
    import { Strings } from '@/locales/types/enums';
    import { useApplicationStore } from "@/stores/Application";
    import { useI18n } from "vue-i18n";
    import SearchInput from "./components/SearchInput/SearchInput.vue";
    import SearchNotFound from "./components/SearchNotFound/SearchNotFound.vue";
    import type { ExposeInterface } from "./types";
    import { useSearchPageStore } from "@/stores/SearchPage";
    import { storeToRefs } from 'pinia';
    import type { SearchResultRef } from "./components/SearchResult/types";
    import type {IEventSelected } from "@/components/RowView/types";

    const SearchPageStore = useSearchPageStore();
    const {
        getHasResult: hasResult,
        getQuery :query,
        getIsLoading: isLoading
    } = storeToRefs(SearchPageStore);

    const ApplicationStore = useApplicationStore();
    const { hideHeader } = ApplicationStore;

    const emit = defineEmits(['show-all', 'list-item-selected', 'ready']);
    const { $eventBus } = useNuxtApp();
    const { t } = useI18n();
    const searchYouCanSearch: string = t(Strings.SEARCH_YOU_CAN_SEARCH).toString();
    const searchResultRef: Ref<SearchResultRef | null> = ref(null); 

    const hasScroll: Ref<boolean> = ref(false);

    const displaySearchResult = computed((): boolean => query.value.length > 0);

    const focusSearchResult = (): void => {
        if (searchResultRef.value) {
            searchResultRef.value.focusFirstRow();
        }
    };
    provide('focusSearchResult', focusSearchResult);

    const focusSearchInput = () => {
        hideHeader();
        document.getElementById('query')?.focus();
    };

    const showAll = (event: IEventSelected): void => {
        emit("show-all", event);
    };
    const ready = (): void => {
        emit('ready');
    };

    const listItemSelected = (event: IEventSelected): void => {
        emit("list-item-selected", event);
    };

    onMounted(() => {
        $eventBus.on("key-up-rows", focusSearchInput);
    });

    onBeforeUnmount(() => {
        $eventBus.off("key-up-rows", focusSearchInput);
    });

    defineExpose<ExposeInterface>({
        focusSearchInput
    });

</script>

<style lang="scss">
    @import url("styles.scss");
</style>
